import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { VideoError, VideoErrorType } from '../video-errors';
import { VideoStatus } from '../enums/video-status.enum';
import { VideoActions } from './actions';
import { DeviceWarningState } from '../enums/device-warning-state.enum';
import { RequestMediaFailedReason } from '../enums/request-media-failed-reason.enum';

export interface VideoState {
  active: boolean;
  initialized: boolean;
  status: VideoStatus;
  connectionProgressPercentage: number;
  playCount: number;
  cameraWarningState: DeviceWarningState;
  microphoneWarningState: DeviceWarningState;
  cameraActive: boolean;
  microphoneActive: boolean;
  retainedCameraActive: boolean;
  retainedMicrophoneActive: boolean;
  error: VideoError;
}

export const initialState: VideoState = {
  active: false,
  initialized: false,
  status: VideoStatus.Disconnected,
  connectionProgressPercentage: 0,
  playCount: 0,
  cameraWarningState: DeviceWarningState.None,
  microphoneWarningState: DeviceWarningState.None,
  cameraActive: false,
  microphoneActive: false,
  retainedCameraActive: false,
  retainedMicrophoneActive: false,
  error: null,
};

const warningStates: Record<RequestMediaFailedReason, DeviceWarningState> = {
  [RequestMediaFailedReason.Blocked]: DeviceWarningState.Blocked,
  [RequestMediaFailedReason.InUse]: DeviceWarningState.InUse,
  [RequestMediaFailedReason.Other]: DeviceWarningState.Other,
};

export function createVideoReducer(actions: VideoActions): ActionReducer<VideoState, Action> {
  return createReducer(
    initialState,
    on(actions.initializationTriggered, (state) => ({
      ...state,
      active: true,
      initialized: false,
      status: VideoStatus.Connecting,
      error: null,
    })),
    on(actions.initializeVideoSucceeded, (state) => ({
      ...state,
      initialized: true,
    })),
    on(actions.connectVideoSucceeded, (state) => ({
      ...state,
      status: VideoStatus.Connected,
    })),
    on(actions.initializeVideoErrored, (state) => ({
      ...state,
      status: VideoStatus.Disconnected,
      error: { type: VideoErrorType.InitializeServiceError },
    })),
    on(actions.connectVideoErrored, (state) => ({
      ...state,
      status: VideoStatus.Disconnected,
      error: { type: VideoErrorType.ConnectError },
    })),
    on(actions.autoPlaySucceeded, actions.manualPlaySucceeded, (state) => ({
      ...state,
      playCount: state.playCount + 1,
      status: VideoStatus.Playing,
      error: null,
    })),
    on(actions.autoPlayErrored, actions.manualPlayErrored, (state) => ({
      ...state,
      error: { type: VideoErrorType.AutoplayError },
    })),
    on(actions.videoConnectionProgressPercentageUpdated, (state, { percentage }) => ({
      ...state,
      connectionProgressPercentage: percentage,
    })),
    on(actions.microphoneAvailable, (state) => ({
      ...state,
      microphoneWarningState: DeviceWarningState.None,
    })),
    on(actions.microphoneNotAvailable, (state) => ({
      ...state,
      microphoneActive: false,
      microphoneWarningState: DeviceWarningState.NotAvailable,
    })),
    on(actions.cameraAvailable, (state) => ({
      ...state,
      cameraWarningState: DeviceWarningState.None,
    })),
    on(actions.cameraNotAvailable, (state) => ({
      ...state,
      cameraActive: false,
      cameraWarningState: DeviceWarningState.NotAvailable,
    })),
    on(
      actions.toggleCameraActiveSucceeded,
      actions.turnMicrophoneAndCameraOnSucceeded,
      (state) => ({
        ...state,
        cameraWarningState: DeviceWarningState.None,
      }),
    ),
    on(
      actions.toggleCameraFailed,
      actions.turnMicrophoneAndCameraOnFailed,
      (state, { reason }) => ({
        ...state,
        cameraWarningState: warningStates[reason],
      }),
    ),
    on(
      actions.toggleMicrophoneActiveSucceeded,
      actions.turnMicrophoneOnSucceeded,
      actions.turnMicrophoneAndCameraOnSucceeded,
      (state) => ({
        ...state,
        microphoneWarningState: DeviceWarningState.None,
      }),
    ),
    on(
      actions.toggleMicrophoneFailed,
      actions.turnMicrophoneOnFailed,
      actions.turnMicrophoneAndCameraOnFailed,
      (state, { reason }) => ({
        ...state,
        microphoneWarningState: warningStates[reason],
      }),
    ),
    on(actions.cameraActiveChanged, (state, { active }) => ({
      ...state,
      cameraActive: active,
    })),
    on(actions.microphoneActiveChanged, (state, { active }) => ({
      ...state,
      microphoneActive: active,
    })),
    on(actions.speakErrored, (state) => ({
      ...state,
      status: VideoStatus.Disconnected,
      error: { type: VideoErrorType.SpeakScriptError },
    })),
    on(actions.serverDisconnected, (state) => ({
      ...state,
      status: VideoStatus.Disconnected,
      error: { type: VideoErrorType.ServerDisconnectedError },
    })),
    on(actions.viewDestroyed, () => ({
      ...initialState,
    })),
  );
}
